import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
  type BookingReorderingCohort,
  bookingReorderingCohortSchema,
} from "../../PlacementCandidate/types";

const notifyRecruiterForScreeningBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.placementCandidateScreening),
    meta: z.object({
      source: z.enum(["banner", "detail-cta"]),
      contactPhone: z.string(),
      bookingReorderingCohort: bookingReorderingCohortSchema.optional(),
      interviewTime: z
        .object({
          start: z.string(), // ISO string
          end: z.string(), // ISO string
        })
        .optional(),
    }),
    relationships: z.object({
      placement: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.placement),
            id: z.string(),
          }),
        })
        .optional(),
    }),
  }),
});

interface NotifyRecruiterForScreeningParams {
  source: "banner" | "detail-cta";
  contactPhone: string;
  placementId?: string;
  bookingReorderingCohort?: BookingReorderingCohort;
  interviewTime?: { start: Date; end: Date };
}

export function useNotifyRecruiterForScreening(
  options: UseMutationOptions<unknown, unknown, NotifyRecruiterForScreeningParams>
) {
  return useMutation({
    mutationFn: async (params: NotifyRecruiterForScreeningParams) => {
      await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/placements/notify-recruiter-for-screening`,
        data: {
          data: {
            type: API_TYPES.placementCandidateScreening,
            meta: {
              source: params.source,
              contactPhone: params.contactPhone,
              ...(isDefined(params.bookingReorderingCohort) && {
                bookingReorderingCohort: params.bookingReorderingCohort,
              }),
              ...(isDefined(params.interviewTime) && {
                interviewTime: {
                  start: params.interviewTime.start.toISOString(),
                  end: params.interviewTime.end.toISOString(),
                },
              }),
            },
            relationships: {
              ...(isDefined(params.placementId) && {
                placement: {
                  data: { type: API_TYPES.placement, id: params.placementId },
                },
              }),
            },
          },
        },
        responseSchema: z.any(),
        requestSchema: notifyRecruiterForScreeningBodySchema,
      });
    },
    ...options,
  });
}
